import * as React from 'react';
import { useState, useRef } from 'react';
import CartaService from '../../../../services/CartaService';
import SelectField from '../../../common/form-controls/SelectField';
import FieldHandler from '../../../../models/interfaces/FieldHandler';
import CartaObjectLookup from '../../../../models/viewModels/CartaId/CartaObjectLookup';
import CartaId from '../../../../models/entities/CartaId';
import ReadOnlyField from './../ReadOnly';
import { useValidation } from './../ValidationContext';
import { Typeahead,TextPassage } from 'react-unity';
import { Grid } from '../../layouts/Grid';
import { Fieldset } from '../../components/Fieldset';
import { Alert } from '../../components/Alert';
import { Tooltip } from '../../components/Tooltip';
import TagGroup from '../../components/TagGroup';
import { Tag } from '../../components/Tag';

interface CartaInputProps extends FieldHandler<CartaId[]> {
	oneOptionOnly?: boolean;
	hideTooltip?: boolean;
	id?: string;
}

const CartaInput = (props: CartaInputProps) => {
	const cartaService = new CartaService();
	const [fieldLabel] = useState<string>('APP ID');
	const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout>(null);
	const [searching, setSearching] = useState<boolean>(false);
	const [issueAlert, setIssueAlert] = useState(null);
	const [cartaSearchResults, setCartaSearchResults] = useState<CartaObjectLookup[]>([]);
	const [retrievedResults, setRetrievedResults] = useState<boolean>(false);
	const searchText = useRef<string>('');
	const typeSelected = useRef<string>('APP');
	const { isValid, nonValidMessage } = useValidation(props.value, props.validation);
	const cartaValues = props.value ?? [];

	const getInitialState = () => {
		searchText.current = '';
		setSearching(false);
		setIssueAlert(null);
		setCartaSearchResults([]);
		setRetrievedResults(false);
	};

	const resetState = () => {
		getInitialState();
	};

	const searchCartaIdInfo = async () => {
		setCartaSearchResults([]);
		const ids = cartaValues.map((cartaId) => cartaId.cartaAppId);
		const appId = `${typeSelected.current}-${searchText.current}`;
		if (ids.includes(appId)) {
			setIssueAlert({ variant: 'warning', message: `${appId} is already in the list.` });
			return;
		}
		const searchString = searchText.current.trim();
		if (searchString?.length <= 3) return;
		setSearching(true);
		try {
			const cartaIdInfo = (await cartaService.searchCartaObjects(searchString, typeSelected.current))
				.filter((cartaInfo) => cartaInfo.status != 'Retired')
				.map((cartaInfo) => new CartaObjectLookup(cartaInfo));
			setCartaSearchResults(cartaIdInfo);
		} catch (error) {
			const HTTP_NOT_FOUND_ERROR_CODE = 404;
			if (error.status !== HTTP_NOT_FOUND_ERROR_CODE) {
				setIssueAlert({
					variant: 'error',
					message: 'An unexpected error occurred while retrieving data from Carta.',
				});
			}
		}
		setSearching(false);
		setRetrievedResults(true);
	};

	const addSearchedCartaId = (cartaId: CartaObjectLookup) => {
		props.onChange([...props.value, { cartaAppId: cartaId.applicationId, cartaAppName: cartaId.name }]);
		resetState();
	};

	const showNonValidMessage = (): boolean => {
		return !isValid && !issueAlert && !retrievedResults;
	};

	const handleOnChangeCartaTypeahead = (): void => {
		if (searchTimeout) clearTimeout(searchTimeout);
		if (typeSelected.current && searchText.current) {
			setSearchTimeout(
				setTimeout(() => {
					searchCartaIdInfo();
				}, 1000)
			);
		}
	};

	const checkIfDMBAlreadySelected = (value) => {
		const cartaId = cartaSearchResults.find((c) => c.applicationId === value);
		const ids = cartaValues.map((c) => c.cartaAppId);
		if (ids.includes(value)) {
			setIssueAlert({
				variant: 'warning',
				message: `${value} is already in the list.`,
			});
		} else {
			addSearchedCartaId(cartaId);
		}
	};

	const handleEventSearch = (event) => {
		typeSelected.current = event.target.value;
		if (searchText.current) searchCartaIdInfo();
		const cartaIds = props.value.filter((t) => t.cartaAppId !== '');
		props.onChange(cartaIds);
	};

	const handleSearchUpdate = (e) => {
		searchText.current = e.target.value;
		setCartaSearchResults([]);
		setRetrievedResults(false);
		handleOnChangeCartaTypeahead();
	};

	const cartaTagGroup = () => (
		<TagGroup closing={!props.disabled} color="em-c-tags__theme-blue">
			{props.value
				.filter((t) => t.cartaAppId !== '')
				.map((cartaId: CartaId) => (
					<Tooltip
						optionalClass="em-u-margin-half"
						content={`${cartaId.cartaAppId}`}
						name={cartaId.cartaAppName}
						disabled={false}
					>
						<Tag
							label={`${cartaId.cartaAppId}`}
							closing={!props.disabled}
							onClick={() => {
								const cartaIds = props.value.filter((c) => c.cartaAppId !== cartaId.cartaAppId);
								props.onChange(cartaIds);
							}}
						/>
					</Tooltip>
				))}
		</TagGroup>
	);

	const disabledView = () => {
		if (cartaValues.length === 0) {
			return (
				<Grid variant="halves">
					<Grid.Item>
						<ReadOnlyField label={fieldLabel} text="" />
					</Grid.Item>
				</Grid>
			);
		}

		return <Fieldset legend={fieldLabel}>{cartaTagGroup()}</Fieldset>;
	};

	const renderedResults =
    retrievedResults && !cartaSearchResults.length
        ? (
            <Typeahead.Item value="0">
                <Typeahead.Item.Suggestion aria-disabled>
                    <i>Object was not found</i>
                </Typeahead.Item.Suggestion>
            </Typeahead.Item>
        )
        : (
            cartaSearchResults.map((cartaId) => (
                <Typeahead.Item
                    key={cartaId.applicationId}
                    value={cartaId.applicationId}
                >
                    <Typeahead.Item.Suggestion>
                        <strong>{cartaId.applicationId}</strong>
                        <span>{` (${cartaId.name})`}</span>
                    </Typeahead.Item.Suggestion>
                </Typeahead.Item>
            ))
        );


	return (
		<>
			{' '}
			{props.disabled && disabledView()}
			{!props.disabled && (!props.oneOptionOnly || cartaValues.length === 0) && (
				<Fieldset optionalClass={!isValid && 'has-error'} legend={fieldLabel}>
					<Grid variant="halves">
						<Grid.Item>
							<div className="em-c-search__body">
								<SelectField
									className="em-u-margin-none"
									value={typeSelected.current}
									options={['APP']}
									onChange={(event) => handleEventSearch(event)}
									disabled
								/>
								<Typeahead
									onChange={(e, value) => {
										checkIfDMBAlreadySelected(value);
									}}
									fieldProps={{
										label: '',
										error: !isValid,
										note: ''
									}}
									inputProps={{
										onChange: handleSearchUpdate,
										placeholder: 'ID or Name',
										value: searchText.current,
										loading: searching
									}}
								>
									{renderedResults}
								</Typeahead>
							</div>
							{showNonValidMessage() && <p>{nonValidMessage}</p>}
							{issueAlert && (
								<Alert
									className="em-u-width-100"
									variant={issueAlert.variant}
									onClose={() => setIssueAlert({ variant: null, message: null })}
									body={issueAlert.message}
								/>
							)}
						</Grid.Item>
						<Grid.Item>{cartaTagGroup()}</Grid.Item>
					</Grid>
				</Fieldset>
			)}
			{!props.disabled && props.oneOptionOnly && cartaValues.length > 0 && (
				<Fieldset legend={fieldLabel}>
					<Grid variant="halves">
						<Grid.Item />
						<Grid.Item>{cartaTagGroup()}</Grid.Item>
					</Grid>
				</Fieldset>
			)}
		</>
	);
};

export default CartaInput;